import { ConfiguredApplicationForm } from './ConfiguredApplicationForm';
import { FormContext, formError } from '../../../../shared/src/application-forms/types';
import { normalizeDocumentMetadata } from '@/utils/applicationDocuments';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Save, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { StepIndicator } from '@/components/auth/StepIndicator';
import { useAuth } from '@/hooks/useAuth';
import { parseCourseSelection } from '@/utils/courseSelection';
import { AgentCourse } from '@/services/agentApi';
import { agentApi } from '@/services/agentApi';
import { ApplicationFormData, ApplicationFormErrors } from '@/types/application';
import { InstitutionSelectionStep } from './steps/InstitutionSelectionStep.tsx';
import { ProfileSelectionStep } from './steps/ProfileSelectionStep.tsx';
import { InstructionsStep } from './steps/InstructionsStep.tsx';
import { QualificationStep } from './steps/QualificationStep.tsx';
import { PersonalInfoStep } from './steps/PersonalInfoStep.tsx';
import { ContactInfoStep } from './steps/ContactInfoStep.tsx';
import { EducationStep } from './steps/EducationStep.tsx';
// import { TestScoresStep } from './steps/TestScoresStep.tsx'; // COMMENTED OUT
import { WorkHistoryStep } from './steps/WorkHistoryStep.tsx';
import { HealthWelfareAccommodationStep } from './steps/HealthWelfareAccommodationStep.tsx';
import { AdditionalInfoStep } from './steps/AdditionalInfoStep.tsx';
import { DocumentsChecklistStep } from './steps/DocumentsChecklistStep.tsx';
import { ReviewStep } from './steps/ReviewStep.tsx';

const STEPS = [
  { id: 1, label: 'Select Institution', component: InstitutionSelectionStep },
  { id: 2, label: 'Load Profile', component: ProfileSelectionStep },
  { id: 3, label: 'Instructions', component: InstructionsStep },
  { id: 4, label: 'Qualification', component: QualificationStep },
  { id: 5, label: 'Personal Info', component: PersonalInfoStep },
  { id: 6, label: 'Contact Info', component: ContactInfoStep },
  { id: 7, label: 'Education', component: EducationStep },
  // { id: 8, label: 'Test Scores', component: TestScoresStep }, // COMMENTED OUT
  { id: 9, label: 'Work History', component: WorkHistoryStep },
  { id: 10, label: 'Health, Welfare...', component: HealthWelfareAccommodationStep },
  { id: 11, label: 'Additional Info', component: AdditionalInfoStep },
  { id: 12, label: 'Documents Checklist', component: DocumentsChecklistStep },
  { id: 13, label: 'Review', component: ReviewStep },
];

const initialFormData: ApplicationFormData = {
  // A. QUALIFICATION YOU ARE APPLYING FOR
  universityId: undefined,
  universityProgramId: undefined,
  programmeStrand: '',
  programmeStartDate: '',
  programmeEndDate: '',
  studiedAtKalandraBefore: false,
  howDidYouFindOut: '',
  howDidYouFindOutWhen: '',
  
  // B. PERSONAL DETAILS
  firstName: '',
  lastName: '',
  middleName: '',
  preferredTitle: '',
  dateOfBirth: '',
  gender: '',
  nsn: '',
  countryOfCitizenship: '',
  email: '',
  ethnicity: '',
  passportNumber: '',
  passportExpiryDate: '',
  iwi: '',
  priorActivity: '',
  disability: false,
  disabilityDetails: '',
  convictionChecks: false,
  convictionDetails: '',
  firstLanguage: '',
  maritalStatus: '',
  
  // C. CONTACT DETAILS
  homeAddress: '', // Legacy - kept for backward compatibility
  homeAddressLine: '',
  homeCity: '',
  homeState: '',
  homeCountry: '',
  homeZipCode: '',
  homePhone: '',
  homeMobile: '',
  homeFax: '',
  homeAccommodationType: '',
  studyAddress: '',
  studyPhone: '',
  studyMobile: '',
  studyFax: '',
  studyAccommodationType: '',
  nextOfKinName: '',
  nextOfKinPhone: '',
  nextOfKinMobile: '',
  nextOfKinFax: '',
  nextOfKinEmail: '',
  nextOfKinAddress: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
  emergencyContactMobile: '',
  emergencyContactEmail: '',
  emergencyContactAddress: '',
  
  // D. ACADEMIC INFORMATION
  secondarySchoolLocation: '',
  secondarySchoolName: '',
  secondarySchoolLastYear: '',
  secondarySchoolHighestLevel: '',
  firstYearTertiaryEnrolment: null,
  tertiaryInstitutionName: '',
  tertiaryInstitutionYear: '',
  englishLevel: '',
  englishExamType: '',
  englishExamDate: '',
  englishOverallScore: '',
  englishListeningScore: '',
  englishReadingScore: '',
  englishWritingScore: '',
  englishSpeakingScore: '',
  hasEnglishScores: false,
  careerIntentions: '',
  
  // D. WORK HISTORY
  workedAsHealthCareAssistant: false,
  healthCareAssistantDuration: '',
  otherHealthcareQualifications: false,
  healthcareQualificationsDetails: '',
  continueStudiesOrWorkforce: '',
  
  // F. HEALTH, WELFARE AND ACCOMMODATION
  insuranceViaSouthernCross: false,
  insuranceViaUniCare: false,
  insuranceSelfArranged: false,
  
  // E. ADDITIONAL INFORMATION
  whyDoYouWantToDoProgramme: '',
  
  // Legacy fields - COMMENTED OUT
  // phoneNumber: '',
  // address: '',
  // city: '',
  // province: '',
  // country: '',
  // postalCode: '',
  // currentEducation: '',
  // levelOfEducation: '',
  // institutionName: '',
  // institutionCountry: '',
  // graduationDate: '',
  // degreeName: '',
  // gpa: '',
  // gradingScheme: '',
  hasGREScores: false,
  // greExamDate: '',
  // greOverallScore: '',
  // greVerbalScore: '',
  // greQuantitativeScore: '',
  // greAnalyticalScore: '',
  hasGMATScores: false,
  // gmatExamDate: '',
  // gmatTotalScore: '',
  hasSATScores: false,
  // satExamDate: '',
  // satOverallScore: '',
  // satMathScore: '',
  // satReadingScore: '',
  // satWritingScore: '',
  // courseOfInterest: '',
  // countryOfChoice: '',
  // budget: '',
  // preferredStartDate: '',
  // studyMode: '',
  // notes: '',
  // documents: '',
  termsAccepted: false,
};

// Helper function to map application data to form data
const mapApplicationToFormData = (app: any): ApplicationFormData => {
  const formatDateForInput = (date: string | Date | null | undefined): string => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  };

  const formatStartDate = (date: string | Date | null | undefined): string => {
    if (!date) return '';
    if (typeof date === 'string') {
      const trimmed = date.trim();
      if (!trimmed) return '';
      if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
        const parsed = new Date(trimmed);
        return isNaN(parsed.getTime()) ? trimmed : parsed.toISOString().split('T')[0];
      }
      return trimmed;
    }
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? '' : parsed.toISOString().split('T')[0];
  };

  return {
    // A. QUALIFICATION
    universityId: app.universityId,
    universityProgramId: app.universityProgramId,
    programmeStrand: app.programmeStrand || '',
    programmeStartDate: formatStartDate(app.programmeStartDate),
    programmeEndDate: formatDateForInput(app.programmeEndDate),
    studiedAtKalandraBefore: app.studiedAtKalandraBefore || false,
    howDidYouFindOut: app.howDidYouFindOut || '',
    howDidYouFindOutWhen: app.howDidYouFindOutWhen || '',
    
    // B. PERSONAL DETAILS
    firstName: app.firstName || '',
    lastName: app.lastName || '',
    middleName: app.middleName || '',
    preferredTitle: app.preferredTitle || '',
    dateOfBirth: formatDateForInput(app.dateOfBirth),
    gender: app.gender || '',
    nsn: app.nsn || '',
    countryOfCitizenship: app.countryOfCitizenship || '',
    email: app.email || '',
    ethnicity: app.ethnicity || '',
    passportNumber: app.passportNumber || '',
    passportExpiryDate: formatDateForInput(app.passportExpiryDate),
    iwi: app.iwi || '',
    priorActivity: app.priorActivity || '',
    disability: app.disability || false,
    disabilityDetails: app.disabilityDetails || '',
    convictionChecks: app.convictionChecks || false,
    convictionDetails: app.convictionDetails || '',
    firstLanguage: app.firstLanguage || '',
    maritalStatus: app.maritalStatus || '',
    
    // C. CONTACT DETAILS
    homeAddress: app.homeAddress || '', // Legacy - kept for backward compatibility
    homeAddressLine: app.homeAddressLine || '',
    homeCity: app.homeCity || '',
    homeState: app.homeState || '',
    homeCountry: app.homeCountry || '',
    homeZipCode: app.homeZipCode || '',
    homePhone: app.homePhone || '',
    homeMobile: app.homeMobile || '',
    homeFax: app.homeFax || '',
    homeAccommodationType: app.homeAccommodationType || '',
    studyAddress: app.studyAddress || '',
    studyPhone: app.studyPhone || '',
    studyMobile: app.studyMobile || '',
    studyFax: app.studyFax || '',
    studyAccommodationType: app.studyAccommodationType || '',
    nextOfKinName: app.nextOfKinName || '',
    nextOfKinPhone: app.nextOfKinPhone || '',
    nextOfKinMobile: app.nextOfKinMobile || '',
    nextOfKinFax: app.nextOfKinFax || '',
    nextOfKinEmail: app.nextOfKinEmail || '',
    nextOfKinAddress: app.nextOfKinAddress || '',
    emergencyContactName: app.emergencyContactName || '',
    emergencyContactPhone: app.emergencyContactPhone || '',
    emergencyContactMobile: app.emergencyContactMobile || '',
    emergencyContactEmail: app.emergencyContactEmail || '',
    emergencyContactAddress: app.emergencyContactAddress || '',
    
    // D. ACADEMIC INFORMATION
    secondarySchoolLocation: app.secondarySchoolLocation || '',
    secondarySchoolName: app.secondarySchoolName || '',
    secondarySchoolLastYear: app.secondarySchoolLastYear || '',
    secondarySchoolHighestLevel: app.secondarySchoolHighestLevel || '',
    firstYearTertiaryEnrolment:
      app.firstYearTertiaryEnrolment === true || app.firstYearTertiaryEnrolment === false
        ? app.firstYearTertiaryEnrolment
        : null,
    tertiaryInstitutionName: app.tertiaryInstitutionName || '',
    tertiaryInstitutionYear: app.tertiaryInstitutionYear || '',
    englishLevel: app.englishLevel || '',
    englishExamType: app.englishExamType || '',
    englishExamDate: formatDateForInput(app.englishExamDate),
    englishOverallScore: app.englishOverallScore || '',
    englishListeningScore: app.englishListeningScore || '',
    englishReadingScore: app.englishReadingScore || '',
    englishWritingScore: app.englishWritingScore || '',
    englishSpeakingScore: app.englishSpeakingScore || '',
    hasEnglishScores: app.hasEnglishScores || false,
    careerIntentions: app.careerIntentions || '',
    
    // D. WORK HISTORY
    workedAsHealthCareAssistant: app.workedAsHealthCareAssistant || false,
    healthCareAssistantDuration: app.healthCareAssistantDuration || '',
    otherHealthcareQualifications: app.otherHealthcareQualifications || false,
    healthcareQualificationsDetails: app.healthcareQualificationsDetails || '',
    continueStudiesOrWorkforce: app.continueStudiesOrWorkforce || '',
    
    // F. HEALTH, WELFARE AND ACCOMMODATION
    insuranceViaSouthernCross: app.insuranceViaSouthernCross || false,
    insuranceViaUniCare: app.insuranceViaUniCare || false,
    insuranceSelfArranged: app.insuranceSelfArranged || false,
    
    // E. ADDITIONAL INFORMATION
    whyDoYouWantToDoProgramme: app.whyDoYouWantToDoProgramme || '',
    
    // Legacy fields - COMMENTED OUT
    // phoneNumber: app.phoneNumber || app.homePhone || app.studyPhone || '',
    // address: app.address || app.homeAddress || app.studyAddress || '',
    // city: app.city || '',
    // province: app.province || '',
    // country: app.country || '',
    // postalCode: app.postalCode || '',
    // currentEducation: app.currentEducation || '',
    // levelOfEducation: app.levelOfEducation || '',
    // institutionName: app.institutionName || '',
    // institutionCountry: app.institutionCountry || '',
    // graduationDate: formatDate(app.graduationDate),
    // degreeName: app.degreeName || '',
    // gpa: app.gpa || '',
    // gradingScheme: app.gradingScheme || '',
    hasGREScores: app.hasGREScores || false,
    // greExamDate: formatDate(app.greExamDate),
    // greOverallScore: app.greOverallScore || '',
    // greVerbalScore: app.greVerbalScore || '',
    // greQuantitativeScore: app.greQuantitativeScore || '',
    // greAnalyticalScore: app.greAnalyticalScore || '',
    hasGMATScores: app.hasGMATScores || false,
    // gmatExamDate: formatDate(app.gmatExamDate),
    // gmatTotalScore: app.gmatTotalScore || '',
    hasSATScores: app.hasSATScores || false,
    // satExamDate: formatDate(app.satExamDate),
    // satOverallScore: app.satOverallScore || '',
    // satMathScore: app.satMathScore || '',
    // satReadingScore: app.satReadingScore || '',
    // satWritingScore: app.satWritingScore || '',
    // courseOfInterest: app.courseOfInterest || '',
    // countryOfChoice: app.countryOfChoice || '',
    // budget: app.budget || '',
    // preferredStartDate: formatDate(app.preferredStartDate),
    // studyMode: app.studyMode || '',
    // notes: app.notes || '',
    documents: normalizeDocumentMetadata(app.documents),
    termsAccepted: app.termsAccepted || false,
  };
};


export const ApplicationFormPage: React.FC = () => {
  const { user } = useAuth();
  return <ApplicationFormSession key={user?.id ?? 'signed-out'} />;
};

const ApplicationFormSession: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [selectedCourse, setSelectedCourse] = useState<AgentCourse | null>(null);
  const { id } = useParams<{ id?: string }>();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<ApplicationFormData>(initialFormData);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const [errors, setErrors] = useState<ApplicationFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [draftApplicationId, setDraftApplicationId] = useState<number | null>(null);
  const [loadedApplicationData, setLoadedApplicationData] = useState<any>(null);
  const [alert, setAlert] = useState<{ type: 'success' | 'danger' | 'warning' | 'info'; message: string; show: boolean } | null>(null);
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const [isPrefilling, setIsPrefilling] = useState(false);
  useEffect(() => {
    setIsPrefilling(false);
    // Existing draft URLs always use saved data, even with forged course parameters.
    if (id) return;
    const controller = new AbortController();
    let selection;
    try { selection = parseCourseSelection(searchParams); }
    catch (error) { setAlert({ type: 'danger', message: (error as Error).message, show: true }); return; }
    if (!selection) return;
    setIsPrefilling(true);
    agentApi.getCourse(selection.offeringId, controller.signal).then(course => {
      if (controller.signal.aborted) return;
      if (!course.canApply || course.universityId !== selection!.universityId) throw new Error('This course is no longer eligible for the selected institution. Please select another course.');
      if (formDataRef.current.universityId || formDataRef.current.universityProgramId) return;
      setFormData(previous => ({
        ...previous, universityId: course.universityId, universityProgramId: course.offeringId,
        programmeStrand: course.programmeName, programmeStartDate: '', programmeEndDate: '',
      }));
      setSelectedCourse(course);
    }).catch(error => {
      if (!controller.signal.aborted) setAlert({ type: 'danger', message: error?.response?.data?.error || error.message || 'Course selection could not be loaded. Please select another course.', show: true });
    }).finally(() => { if (!controller.signal.aborted) setIsPrefilling(false); });
    return () => controller.abort();
    // Consume a course handoff once per account/new application. User edits win afterwards.
  }, [id, user?.id]);

  // Load draft application if ID is provided (only on initial load, not when URL changes after save)
  useEffect(() => {
    const loadDraftApplication = async () => {
      if (!id) return;
      
      // If we already have this draft loaded, don't reload it
      if (draftApplicationId && draftApplicationId === parseInt(id)) {
        return;
      }
      
      setIsLoading(true);
      try {
        const application = await agentApi.getApplication(parseInt(id));
        
        // Check if it's a draft
        if (application.status !== 'DRAFT') {
          setAlert({ type: 'warning', message: 'This application has already been submitted and cannot be edited.', show: true });
          setTimeout(() => {
            navigate('/applications');
          }, 2000);
          return;
        }
        
        // Map application data to form data
        const mappedData = mapApplicationToFormData(application);
        setFormData(mappedData);
        setDraftApplicationId(application.id);
        
        // Store full application data for ReviewStep to access university/program names
        setLoadedApplicationData(application);
        
        // Always start from step 1 when editing a draft (only on initial load)
        if (!draftApplicationId) {
          setCurrentStep(1);
        }
      } catch (error: any) {
        console.error('Error loading draft application:', error);
        // Extract error message from API response
        const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Unknown error';
        setAlert({ type: 'danger', message: 'Failed to load draft application: ' + errorMessage, show: true });
        setTimeout(() => {
          navigate('/applications');
        }, 3000);
      } finally {
        setIsLoading(false);
      }
    };

    loadDraftApplication();
  }, [id]); // Removed navigate from dependencies to prevent unnecessary reloads

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const validateStep = (step: number): boolean => {
    const newErrors: ApplicationFormErrors = {};

    switch (step) {
      case 1: // Institution Selection
        if (!formData.universityId) newErrors.universityId = 'Please select an institution';
        break;

      case 2: // Profile Selection
        // Profile selection is optional
        break;

      case 3: // Instructions
        // Instructions step has no validation
        break;

      case 4: // Qualification
        if (!formData.programmeStrand?.trim()) {
          newErrors.programmeStrand = 'Programme strand is required';
        }
        if (!formData.programmeStartDate?.trim()) {
          newErrors.programmeStartDate = 'Intake is required';
        }
        if (!formData.howDidYouFindOut?.trim()) {
          newErrors.howDidYouFindOut = 'Please tell us how you found out about this institution';
        }
        break;

      case 5: // Personal Info
        if (!formData.preferredTitle?.trim()) newErrors.preferredTitle = 'Preferred title is required';
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newErrors.email = 'Please enter a valid email address';
        }
        if (!formData.ethnicity || !formData.ethnicity.trim()) newErrors.ethnicity = 'Ethnicity is required';
        if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
        if (!formData.gender) newErrors.gender = 'Gender is required';
        if (!formData.maritalStatus) newErrors.maritalStatus = 'Marital status is required';
        if (!formData.countryOfCitizenship) newErrors.countryOfCitizenship = 'Country of citizenship is required';
        if (!formData.firstLanguage || !formData.firstLanguage.trim()) newErrors.firstLanguage = 'First language is required';
        if (!formData.passportExpiryDate) newErrors.passportExpiryDate = 'Passport expiry date is required';
        if (!formData.priorActivity || !formData.priorActivity.trim()) newErrors.priorActivity = 'Prior activity is required';
        break;

      case 6: // Contact Info
        // Home Country Contact - all required except Home Fax
        if (!formData.homeAddressLine?.trim()) newErrors.homeAddressLine = 'Home address line is required';
        if (!formData.homeCity?.trim()) newErrors.homeCity = 'Home city/town is required';
        if (!formData.homeState?.trim()) newErrors.homeState = 'Home state is required';
        if (!formData.homeCountry) newErrors.homeCountry = 'Home country is required';
        if (!formData.homeZipCode?.trim()) newErrors.homeZipCode = 'Home ZIP/postal code is required';
        if (!formData.homePhone?.trim()) newErrors.homePhone = 'Home phone is required';
        if (!formData.homeMobile?.trim()) newErrors.homeMobile = 'Home mobile is required';
        // Next of Kin/Parents in Home Country - all required except Fax
        if (!formData.nextOfKinName?.trim()) newErrors.nextOfKinName = 'Next of kin name is required';
        if (!formData.nextOfKinEmail?.trim()) newErrors.nextOfKinEmail = 'Next of kin email is required';
        if (!formData.nextOfKinPhone?.trim()) newErrors.nextOfKinPhone = 'Next of kin phone is required';
        if (!formData.nextOfKinMobile?.trim()) newErrors.nextOfKinMobile = 'Next of kin mobile is required';
        if (!formData.nextOfKinAddress?.trim()) newErrors.nextOfKinAddress = 'Next of kin address is required';
        break;

      case 7: // Education
        // Academic information - Secondary School Information + key academic fields are compulsory
        if (!formData.secondarySchoolLocation) {
          newErrors.secondarySchoolLocation = 'Please select whether your secondary schooling was in New Zealand or overseas';
        }
        if (!formData.secondarySchoolName?.trim()) {
          newErrors.secondarySchoolName = 'Last secondary school attended is required';
        }
        if (!formData.secondarySchoolLastYear?.trim()) {
          newErrors.secondarySchoolLastYear = 'Last year at secondary school is required';
        }
        if (!formData.secondarySchoolHighestLevel?.trim()) {
          newErrors.secondarySchoolHighestLevel = 'Highest level achieved is required';
        } else if (
          formData.secondarySchoolHighestLevel === 'Other' ||
          formData.secondarySchoolHighestLevel === 'Other -'
        ) {
          newErrors.secondarySchoolHighestLevel = 'Please specify your highest level achieved';
        }
        if (formData.firstYearTertiaryEnrolment === null) {
          newErrors.firstYearTertiaryEnrolment = 'Please select whether this is your first year in tertiary education';
        }
        if (!formData.englishLevel) {
          newErrors.englishLevel = 'Please select your English language level';
        }
        if (!formData.careerIntentions || !formData.careerIntentions.trim()) {
          newErrors.careerIntentions = 'Please describe your career intentions';
        }
        break;

      case 8: // Work History
        if (!formData.continueStudiesOrWorkforce?.trim()) {
          newErrors.continueStudiesOrWorkforce = 'Please select your intended plan after completing the course';
        }
        break;

      case 9: // Health, Welfare & Accommodation
        // Insurance is compulsory – at least one option must be selected
        if (
          !formData.insuranceViaSouthernCross &&
          !formData.insuranceViaUniCare &&
          !formData.insuranceSelfArranged
        ) {
          newErrors.insuranceViaSouthernCross = 'Please select at least one insurance option';
        }
        break;

      case 10: // Additional Info
        if (!formData.whyDoYouWantToDoProgramme || !formData.whyDoYouWantToDoProgramme.trim()) {
          newErrors.whyDoYouWantToDoProgramme = 'This field is required';
        }
        break;

      case 11: // Documents Checklist
        // Documents - copy of passport is compulsory
        try {
          const docsMeta = formData.documents ? JSON.parse(formData.documents) : {};
          const passportMeta = docsMeta.passport;
          if (!passportMeta || !passportMeta.id) {
            newErrors.documents = 'Copy of passport is required before proceeding';
          }
        } catch {
          newErrors.documents = 'Copy of passport is required before proceeding';
        }
        break;

      case 12: // Review
        // Final validation before submit
        if (!formData.universityId) newErrors.universityId = 'Please select an institution';
        if (!formData.preferredTitle?.trim()) newErrors.preferredTitle = 'Preferred title is required';
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!formData.ethnicity || !formData.ethnicity.trim()) newErrors.ethnicity = 'Ethnicity is required';
        if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
        if (!formData.gender) newErrors.gender = 'Gender is required';
        if (!formData.maritalStatus) newErrors.maritalStatus = 'Marital status is required';
        if (!formData.countryOfCitizenship) newErrors.countryOfCitizenship = 'Country of citizenship is required';
        if (!formData.firstLanguage || !formData.firstLanguage.trim()) newErrors.firstLanguage = 'First language is required';
        if (!formData.passportExpiryDate) newErrors.passportExpiryDate = 'Passport expiry date is required';
        if (!formData.priorActivity || !formData.priorActivity.trim()) newErrors.priorActivity = 'Prior activity is required';
        if (!formData.secondarySchoolLocation) {
          newErrors.secondarySchoolLocation = 'Please select whether your secondary schooling was in New Zealand or overseas';
        }
        if (!formData.secondarySchoolName?.trim()) {
          newErrors.secondarySchoolName = 'Last secondary school attended is required';
        }
        if (!formData.secondarySchoolLastYear?.trim()) {
          newErrors.secondarySchoolLastYear = 'Last year at secondary school is required';
        }
        if (!formData.secondarySchoolHighestLevel?.trim()) {
          newErrors.secondarySchoolHighestLevel = 'Highest level achieved is required';
        } else if (
          formData.secondarySchoolHighestLevel === 'Other' ||
          formData.secondarySchoolHighestLevel === 'Other -'
        ) {
          newErrors.secondarySchoolHighestLevel = 'Please specify your highest level achieved';
        }
        if (formData.firstYearTertiaryEnrolment === null) {
          newErrors.firstYearTertiaryEnrolment = 'Please select whether this is your first year in tertiary education';
        }
        if (!formData.englishLevel) {
          newErrors.englishLevel = 'Please select your English language level';
        }
        if (!formData.careerIntentions || !formData.careerIntentions.trim()) {
          newErrors.careerIntentions = 'Please describe your career intentions';
        }
        if (!formData.whyDoYouWantToDoProgramme || !formData.whyDoYouWantToDoProgramme.trim()) {
          newErrors.whyDoYouWantToDoProgramme = 'This field is required';
        }
        // Ensure passport document uploaded
        try {
          const docsMeta = formData.documents ? JSON.parse(formData.documents) : {};
          const passportMeta = docsMeta.passport;
          if (!passportMeta || !passportMeta.id) {
            newErrors.documents = 'Copy of passport is required before submitting';
          }
        } catch {
          newErrors.documents = 'Copy of passport is required before submitting';
        }
        // Insurance is compulsory – at least one option must be selected
        if (
          !formData.insuranceViaSouthernCross &&
          !formData.insuranceViaUniCare &&
          !formData.insuranceSelfArranged
        ) {
          newErrors.insuranceViaSouthernCross = 'Please select at least one insurance option';
        }
        // Home Country Contact - all required except Home Fax
        if (!formData.homeAddressLine?.trim()) newErrors.homeAddressLine = 'Home address line is required';
        if (!formData.homeCity?.trim()) newErrors.homeCity = 'Home city/town is required';
        if (!formData.homeState?.trim()) newErrors.homeState = 'Home state is required';
        if (!formData.homeCountry) newErrors.homeCountry = 'Home country is required';
        if (!formData.homeZipCode?.trim()) newErrors.homeZipCode = 'Home ZIP/postal code is required';
        if (!formData.homePhone?.trim()) newErrors.homePhone = 'Home phone is required';
        if (!formData.homeMobile?.trim()) newErrors.homeMobile = 'Home mobile is required';
        // Next of Kin/Parents in Home Country - all required except Fax
        if (!formData.nextOfKinName?.trim()) newErrors.nextOfKinName = 'Next of kin name is required';
        if (!formData.nextOfKinEmail?.trim()) newErrors.nextOfKinEmail = 'Next of kin email is required';
        if (!formData.nextOfKinPhone?.trim()) newErrors.nextOfKinPhone = 'Next of kin phone is required';
        if (!formData.nextOfKinMobile?.trim()) newErrors.nextOfKinMobile = 'Next of kin mobile is required';
        if (!formData.nextOfKinAddress?.trim()) newErrors.nextOfKinAddress = 'Next of kin address is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        if (!formData.programmeStrand?.trim()) newErrors.programmeStrand = 'Programme strand is required';
        if (!formData.programmeStartDate?.trim()) newErrors.programmeStartDate = 'Intake is required';
        if (!formData.howDidYouFindOut?.trim()) {
          newErrors.howDidYouFindOut = 'Please tell us how you found out about this institution';
        }
        if (!formData.continueStudiesOrWorkforce?.trim()) {
          newErrors.continueStudiesOrWorkforce = 'Please select your intended plan after completing the course';
        }
        // Check terms acceptance
        if (!formData.termsAccepted) {
          newErrors.termsAccepted = 'You must accept the Terms and Conditions to submit this application';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Ensure a draft application exists (required for document uploads). Creates one if needed.
  const ensureDraftApplication = async (): Promise<boolean> => {
    if (draftApplicationId) return true;
    setIsSaving(true);
    try {
      const application = await agentApi.createApplication({ ...formData, status: 'DRAFT' });
      setDraftApplicationId(application.id);
      if (!id) window.history.replaceState({}, '', `/applications/${application.id}/edit`);
      return true;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Could not create draft';
      setAlert({ type: 'danger', message: errorMessage, show: true });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    // Validate the current step before moving on
    if (!validateStep(currentStep)) return;
    if (currentStep >= STEPS.length) return;

    // Documents step (12) requires a draft so uploads use the agent application endpoint
    const nextStep = currentStep + 1;
    const isMovingToDocuments = nextStep === 12;
    if (isMovingToDocuments && !draftApplicationId) {
      const created = await ensureDraftApplication();
      if (!created) return;
      toast.success('Draft saved. You can now upload documents.');
    }

    setCurrentStep(nextStep);
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      const applicationData = {
        ...formData,
        status: 'DRAFT',
      };
      
      let application;
      const currentStepBeforeSave = currentStep; // Preserve current step
      
      if (draftApplicationId) {
        // Update existing draft
        application = await agentApi.updateApplication(draftApplicationId, applicationData);
      } else {
        // Create new draft
        application = await agentApi.createApplication(applicationData);
        setDraftApplicationId(application.id);
        // Update URL if we're creating a new draft (but don't trigger reload)
        if (!id) {
          // Use replace to update URL without triggering navigation effects
          window.history.replaceState({}, '', `/applications/${application.id}/edit`);
        }
      }
      
      // Restore the current step after save
      setCurrentStep(currentStepBeforeSave);
      
      // Show success toast
      toast.success('Application saved as draft successfully!');
    } catch (error: any) {
      console.error('Error saving draft:', error);
      // Extract error message from API response
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Unknown error';
      setAlert({ type: 'danger', message: errorMessage, show: true });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    // Validate the Review step (last step)
    if (!validateStep(STEPS.length)) {
      // Stay on the Review step to show errors
      // Scroll to top to ensure error messages are visible
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Prompt to save as profile if there were changes or if no profile was used
    const shouldPromptSave = selectedProfileId ? hasChanges : true;
    let saveAsProfile = false;
    let profileIdToUpdate: number | undefined = undefined;

    if (shouldPromptSave) {
      const saveConfirmation = window.confirm(
        selectedProfileId
          ? 'Would you like to update the saved student profile with the changes made to this application?'
          : 'Would you like to save this application data as a student profile for future use?'
      );
      
      if (saveConfirmation) {
        saveAsProfile = true;
        if (selectedProfileId) {
          profileIdToUpdate = selectedProfileId;
        }
      }
    }

    setIsSubmitting(true);
    try {
      // First, save/update the application as a draft with all the latest data
      const applicationData = {
        ...formData,
        status: 'DRAFT', // Keep as draft until we explicitly submit
      };
      
      let application;
      if (draftApplicationId) {
        // Update existing draft with latest data
        application = await agentApi.updateApplication(draftApplicationId, applicationData);
      } else {
        // Create new application as draft
        application = await agentApi.createApplication(applicationData);
      }
      
      // Now submit the application (this will change status to SUBMITTED and set submittedAt)
      // Pass saveAsProfile flag and profileId if updating
      await agentApi.submitApplication(application.id, saveAsProfile, profileIdToUpdate);
      
      // Mark as submitted to hide submit button
      setIsSubmitted(true);
      
      // Show success toast and navigate
      toast.success('Application submitted successfully!');
      setTimeout(() => {
        navigate('/applications');
      }, 1500);
    } catch (error: any) {
      console.error('Error submitting application:', error);
      // Extract error message from API response
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Unknown error';
      setAlert({ type: 'danger', message: errorMessage, show: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormData = (updates: Partial<ApplicationFormData>) => {
    setFormData(prev => {
      const newData = { ...prev, ...updates };
      if (Object.prototype.hasOwnProperty.call(updates, 'universityId') && updates.universityId !== prev.universityId) {
        newData.universityProgramId = undefined;
        newData.programmeStrand = '';
        newData.programmeStartDate = '';
        newData.programmeEndDate = '';
      }
      // Track if there are changes from the original profile
      if (selectedProfileId) {
        setHasChanges(true);
      }
      return newData;
    });
  };

  const handleProfileSelected = (profile: any) => {
    setSelectedProfileId(profile.id);
    setHasChanges(false); // Reset changes flag when profile is selected
  };

  const [institutionForm, setInstitutionForm] = useState<{ universityId: number; data?: FormContext; error?: string }>();
  const [formRetry, setFormRetry] = useState(0);
  const universityId = Number(formData.universityId);
  useEffect(() => {
    if (!universityId) return;
    const controller = new AbortController();
    setInstitutionForm({ universityId });
    void agentApi.getApplicationForm(universityId, controller.signal).then(data => {
      if (!controller.signal.aborted) setInstitutionForm({ universityId, data });
    }).catch(error => {
      if (!controller.signal.aborted) setInstitutionForm({ universityId, error: formError(error) });
    });
    return () => controller.abort();
  }, [universityId, formRetry]);

  const backFromInstitution = () => {
    if (id) navigate('/applications');
    else {
      setCurrentStep(1);
      setSelectedCourse(null);
      setFormData(previous => ({ ...previous, universityId: undefined, universityProgramId: undefined, programmeStrand: '', programmeStartDate: '', programmeEndDate: '' }));
    }
  };
  const context = institutionForm?.universityId === universityId ? institutionForm : undefined;
  if (universityId && !isLoading && !isPrefilling) {
    if (context?.data?.revision) return <ConfiguredApplicationForm key={universityId} universityId={universityId}
      initialOffering={Number(formData.universityProgramId) || undefined} applicationId={id ? Number(id) : undefined}
      revision={context.data.revision} onBack={backFromInstitution} />;
    if (!context?.data || context.data.required) return <section className="container py-4">
      <button type="button" className="btn btn-link ps-0" onClick={backFromInstitution}>Back to institution selection</button>
      {context?.error || context?.data?.required ? <div className="alert alert-warning" role="alert">
        {context?.error || 'This institution’s application form is currently unavailable.'}
        <button type="button" className="btn btn-link" onClick={() => setFormRetry(value => value + 1)}>Retry</button>
      </div> : <p role="status">Checking institution form…</p>}
    </section>;
  }

  const CurrentStepComponent = STEPS[currentStep - 1]?.component;

  if (isLoading || isPrefilling) {
    return (
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12">
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading application...</span>
              </div>
              <p className="mt-3 text-muted">Loading application...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Helper function to dismiss alert
  const dismissAlert = () => {
    setAlert(null);
  };

  return (
    <div className="container-fluid py-3">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-10">
          {/* Bootstrap Alert */}
          {alert && alert.show && (
            <div className={`alert alert-${alert.type} alert-dismissible fade show mb-3`} role="alert">
              {alert.message}
              <button
                type="button"
                className="btn-close"
                onClick={dismissAlert}
                aria-label="Close"
              ></button>
            </div>
          )}

          <div className="d-flex align-items-center mb-3">
            <button
              className="btn btn-link p-0 me-2"
              onClick={() => navigate('/applications')}
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="h4 fw-bold text-dark mb-0">
                {id ? 'Continue Application' : 'New Application Form'}
              </h1>
              <p className="text-muted small mb-0">
                {id 
                  ? 'Continue filling out your draft application'
                  : 'Complete the student application form following the template structure'
                }
              </p>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-body p-3">
              <StepIndicator
                currentStep={currentStep}
                totalSteps={STEPS.length}
                stepLabels={STEPS.map(s => s.label)}
            onStepClick={(step) => {
              // Only allow navigating to the same or previous steps via the step indicator.
              // Forward navigation must go through the Next button so validation for each step runs.
              if (step <= currentStep) {
                setCurrentStep(step);
              }
            }}
              />

              <AnimatePresence mode="wait">
                {CurrentStepComponent && (
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {currentStep === 2 ? (
                      <ProfileSelectionStep
                        formData={formData}
                        errors={errors}
                        updateFormData={updates => {
                          if (selectedCourse && formData.universityProgramId === selectedCourse.offeringId) {
                            const { universityId, universityProgramId, programmeStrand, programmeStartDate, programmeEndDate, ...profileFields } = updates;
                            updateFormData(profileFields);
                          } else updateFormData(updates);
                        }}
                        setErrors={setErrors}
                        draftApplicationId={draftApplicationId}
                        applicationData={loadedApplicationData}
                        onProfileSelected={handleProfileSelected}
                      />
                    ) : (
                      <CurrentStepComponent
                        formData={formData}
                        errors={errors}
                        updateFormData={updateFormData}
                        setErrors={setErrors}
                        draftApplicationId={draftApplicationId}
                        applicationData={loadedApplicationData}
                        selectedCourse={selectedCourse}
                      />
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                <div>
                  {currentStep > 1 && (
                    <button
                      className="btn btn-outline-secondary"
                      onClick={handlePrevious}
                      disabled={isSubmitting || isSaving || isSubmitted}
                    >
                      <ArrowLeft size={18} className="me-2" />
                      Previous
                    </button>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-primary"
                    onClick={handleSaveDraft}
                    disabled={isSubmitting || isSaving || isSubmitted}
                  >
                    <Save size={18} className="me-2" />
                    {isSaving ? 'Saving...' : 'Save Draft'}
                  </button>
                  {currentStep < STEPS.length ? (
                    <button
                      className="btn btn-primary"
                      onClick={handleNext}
                      disabled={isSubmitting || isSaving || isSubmitted}
                    >
                      Next
                      <ArrowRight size={18} className="ms-2" />
                    </button>
                  ) : (
                    !isSubmitted && (
                      <button
                        className="btn btn-success"
                        onClick={handleSubmit}
                        disabled={isSubmitting || isSaving || isSubmitted}
                      >
                        <Send size={18} className="me-2" />
                        {isSubmitting ? 'Submitting...' : 'Submit Application'}
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
